'use client'

import { useRef, useState } from 'react'

import { useMutation } from '@tanstack/react-query'
import { ArrowUpDown } from 'lucide-react'
import { useForm, useWatch } from 'react-hook-form'
import { formatUnits } from 'viem'
import {
  Connector,
  useAccount,
  useConnect,
  useConnections,
  useConnectors,
  useSwitchAccount,
} from 'wagmi'

import {
  AddressDisplay,
  Button,
  List,
  LoadingSpinner,
  Message,
  NumberInput,
  TokenBalance,
  TokenDisplay,
} from '~/components/component-library'
import {
  isPrivySyncedConnector,
  useDetectedWallets,
} from '~/components/forms/deposit/wallet/detectedWallets'
import {
  useWalletDepositBalances,
  WalletTokenBalance,
} from '~/components/forms/deposit/wallet/useWalletDepositBalances'
import { useWalletDepositTransfer } from '~/components/forms/deposit/wallet/useWalletDepositTransfer'
import {
  formatWalletErrorMessage,
  getGasSymbol,
  getNoGasReason,
  tryParseAmount,
} from '~/components/forms/deposit/wallet/utils'
import { SupportedChainId } from '~/constants/chains'
import { useTokenMetadata } from '~/hooks/useTokenMetadata'
import { useTokenPrices } from '~/hooks/useTokenPrices'
import { useDenominationStore } from '~/store/denominationStore'
import { OrgSmartAccount } from '~/types'
import { EvmAddress } from '~/utils/address'
import { displayCurrency } from '~/utils/display'
import { isNativeTokenAddress } from '~/utils/tokens'

export const WalletDepositSection = ({
  account,
  chainIds,
}: {
  account: OrgSmartAccount
  chainIds: SupportedChainId[]
}) => {
  const detectedWallets = useDetectedWallets()
  const walletConnectConnectors = useConnectors().filter(
    (connector): connector is RainbowKitConnector =>
      connector.id === 'walletConnect',
  )
  const walletConnectConnector =
    walletConnectConnectors.find(
      (connector) => connector.rkDetails?.showQrModal,
    ) ??
    (walletConnectConnectors.length === 1
      ? walletConnectConnectors[0]
      : undefined)
  const walletOptions: WalletOption[] = [
    ...detectedWallets.map((wallet) => ({
      key: wallet.rdns,
      name: wallet.name,
      icon: wallet.icon,
      connector: wallet.connector,
    })),
    ...(walletConnectConnector
      ? [
          {
            key: walletConnectConnector.uid,
            name: walletConnectConnector.name,
            icon:
              walletConnectConnector.icon ?? '/wallets/walletconnect-logo.svg',
            connector: walletConnectConnector,
          },
        ]
      : []),
  ]
  const { address, connector } = useAccount()
  // UI state only — never disconnect(): the Coinbase SDK reloads the page.
  const [isChangingWallet, setIsChangingWallet] = useState(false)

  // A Privy embedded wallet is the session's signer, not a deposit source.
  const walletAddress =
    connector && address && !isPrivySyncedConnector(connector)
      ? EvmAddress.format(address)
      : undefined

  if (!walletAddress && walletOptions.length === 0) return null

  if (!walletAddress || (isChangingWallet && walletOptions.length > 0)) {
    return (
      <WalletList
        wallets={walletOptions}
        onWalletPicked={() => setIsChangingWallet(false)}
      />
    )
  }

  const activeWallet = detectedWallets.find(
    (wallet) => wallet.connector.uid === connector?.uid,
  )
  const walletName = activeWallet?.name ?? connector?.name
  const walletIcon = activeWallet?.icon ?? connector?.icon

  return (
    <ConnectedWalletDeposit
      walletAddress={walletAddress}
      account={account}
      chainIds={chainIds}
      walletDisplay={{ name: walletName, icon: walletIcon }}
      canChangeWallet={walletOptions.length > 0}
      onChangeWallet={() => setIsChangingWallet(true)}
    />
  )
}

const isAlreadyConnectedError = (error: unknown) =>
  error instanceof Error && error.name === 'ConnectorAlreadyConnectedError'

type WalletOption = {
  key: string
  name: string
  icon?: string
  connector: Connector
}

type RainbowKitConnector = Connector & {
  rkDetails?: { showQrModal?: boolean }
}

const WalletList = ({
  wallets,
  onWalletPicked,
}: {
  wallets: WalletOption[]
  onWalletPicked: () => void
}) => {
  const connections = useConnections()
  const { connectAsync } = useConnect()
  const { switchAccountAsync } = useSwitchAccount()

  // connect() rejects a connector with a live connection — the norm after
  // "Change wallet", which never disconnects — so switch to it instead.
  const selectWallet = useMutation({
    onSuccess: () => {
      onWalletPicked()
    },
    mutationFn: async (wallet: WalletOption) => {
      const hasConnection = connections.some(
        (connection) => connection.connector.uid === wallet.connector.uid,
      )
      if (hasConnection) {
        await switchAccountAsync({ connector: wallet.connector })
        return
      }
      try {
        await connectAsync({ connector: wallet.connector })
      } catch (error) {
        if (!isAlreadyConnectedError(error)) throw error
        await switchAccountAsync({ connector: wallet.connector })
      }
    },
  })

  return (
    <div className="space-y-4">
      <List
        label="Choose a wallet"
        items={wallets.map((wallet) => ({
          key: wallet.key,
          content: wallet.name,
          image: wallet.icon ? (
            <img
              src={wallet.icon}
              alt=""
              className="h-6 w-6 rounded-md object-contain"
            />
          ) : undefined,
          meta:
            selectWallet.isPending &&
            selectWallet.variables?.key === wallet.key ? (
              <LoadingSpinner size="xl" thickness="normal" variant="default" />
            ) : undefined,
          isDisabled:
            selectWallet.isPending &&
            selectWallet.variables?.key !== wallet.key,
          onClick: () => {
            selectWallet.mutate(wallet)
          },
        }))}
      />
      {!selectWallet.isPending && selectWallet.error && (
        <Message variant="danger" title="Could not connect">
          {formatWalletErrorMessage(selectWallet.error)}
        </Message>
      )}
    </div>
  )
}

const ConnectedWalletDeposit = ({
  walletAddress,
  account,
  chainIds,
  walletDisplay,
  canChangeWallet,
  onChangeWallet,
}: {
  walletAddress: EvmAddress.Type
  account: OrgSmartAccount
  chainIds: SupportedChainId[]
  walletDisplay: { name?: string; icon?: string }
  canChangeWallet: boolean
  onChangeWallet: () => void
}) => {
  const [selectedToken, setSelectedToken] = useState<WalletTokenBalance | null>(
    null,
  )
  const { balances, isLoading } = useWalletDepositBalances({
    address: walletAddress,
    chainIds,
  })
  const transfer = useWalletDepositTransfer({
    treasuryAddress: EvmAddress.format(account.address),
  })

  const resetFlow = () => {
    setSelectedToken(null)
    transfer.resetTransfer()
  }

  // No action on purpose: completing FUND swaps this whole surface out.
  if (transfer.isSuccess) {
    return (
      <Message variant="success" title="Deposit sent">
        {`Your transfer is confirmed and will appear in ${account.name} soon.`}
      </Message>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          {walletDisplay.icon && (
            <img
              src={walletDisplay.icon}
              alt={walletDisplay.name ?? 'Connected wallet'}
              title={walletDisplay.name}
              className="h-5 w-5 rounded"
            />
          )}
          <AddressDisplay
            address={walletAddress}
            layout="standard"
            hidePopover
          />
        </div>
        {canChangeWallet && (
          <Button
            variant="tertiary"
            size="small"
            eventName="clickedChangeDepositWallet"
            onClick={() => {
              resetFlow()
              onChangeWallet()
            }}
          >
            Change wallet
          </Button>
        )}
      </div>
      {selectedToken ? (
        <DepositAmountForm
          token={selectedToken}
          balances={balances}
          transfer={transfer}
          onBack={resetFlow}
        />
      ) : (
        <WalletBalancesList
          balances={balances}
          isLoading={isLoading}
          onSelect={setSelectedToken}
        />
      )}
    </div>
  )
}

const WalletBalancesList = ({
  balances,
  isLoading,
  onSelect,
}: {
  balances: WalletTokenBalance[]
  isLoading: boolean
  onSelect: (balance: WalletTokenBalance) => void
}) => {
  if (balances.length === 0) {
    if (isLoading) {
      return (
        <div className="flex min-h-20 items-center justify-center">
          <LoadingSpinner size="xl" thickness="normal" variant="default" />
        </div>
      )
    }
    return (
      <Message variant="info" title="No assets found">
        This wallet holds nothing on the networks this treasury supports. Copy
        the address below to send from an exchange or another wallet.
      </Message>
    )
  }

  return (
    <div className="max-h-96 overflow-y-auto rounded-lg">
      <List
        label="Choose an asset to deposit"
        items={balances.map((balance) => ({
          key: `${balance.chainId}-${balance.address}`,
          content: (
            <TokenDisplay
              layout="two-line"
              address={balance.address}
              chainId={balance.chainId}
            />
          ),
          meta: (
            <TokenBalance
              address={balance.address}
              chainId={balance.chainId}
              tokenAmount={balance.amount}
              layout="standard"
              size="small"
              direction="neutral"
            />
          ),
          onClick: () => onSelect(balance),
        }))}
      />
    </div>
  )
}

const getSendButtonLabel = ({
  isTransferring,
  transferPhase,
}: {
  isTransferring: boolean
  transferPhase: 'switching' | 'signing' | 'confirming'
}) => {
  if (!isTransferring) return 'Send deposit'
  if (transferPhase === 'switching') return 'Switch network in your wallet'
  if (transferPhase === 'signing') return 'Confirm in your wallet'
  return 'Waiting for confirmation'
}

const DepositAmountForm = ({
  token,
  balances,
  transfer,
  onBack,
}: {
  token: WalletTokenBalance
  balances: WalletTokenBalance[]
  transfer: ReturnType<typeof useWalletDepositTransfer>
  onBack: () => void
}) => {
  const tokenMetadata = useTokenMetadata({
    chainId: token.chainId,
    address: token.address,
  })
  const { getPrice, convertTokenToUSD, convertUSDToToken } = useTokenPrices()
  const isUsdPreferred = useDenominationStore((state) => state.isUsdMode)
  const toggleDenomination = useDenominationStore((state) => state.toggle)
  const decimals = tokenMetadata?.decimals ?? 18
  const symbol = tokenMetadata?.symbol ?? ''
  const availableAmount = formatUnits(token.amount, decimals)
  const price = getPrice(token.chainId, token.address)
  const canShowUsd = !!price && price > 0
  const isUsdMode = isUsdPreferred && canShowUsd

  const { control, handleSubmit, setValue, getValues } = useForm<{
    amount: string
  }>({
    defaultValues: { amount: '' },
  })
  const amountValue = useWatch({ control, name: 'amount' }) ?? ''

  // USD display rounds to cents, so Max keeps the exact balance aside and uses
  // it until the user edits the amount.
  const maxTokenAmount = useRef<bigint | null>(null)

  const noGasReason = getNoGasReason({ token, balances })

  const toTokenAmount = (text: string): bigint | null => {
    if (maxTokenAmount.current !== null) return maxTokenAmount.current
    if (!isUsdMode) return tryParseAmount(text, decimals)
    const usdValue = Number(text.replace(/,/g, ''))
    if (!Number.isFinite(usdValue)) return null
    return tryParseAmount(
      convertUSDToToken(usdValue, token.address, token.chainId),
      decimals,
    )
  }

  const toUsdValue = (tokenAmount: bigint) =>
    convertTokenToUSD({
      token: token.address,
      amount: tokenAmount,
      chainId: token.chainId,
    }) ?? 0

  const validateAmount = (text: string) => {
    const parsed = toTokenAmount(text)
    if (parsed === null || parsed <= 0n) return 'Enter an amount to deposit'
    if (parsed > token.amount) return 'Amount exceeds the wallet balance'
    if (isNativeTokenAddress(token.address) && parsed === token.amount) {
      return `Leave some ${getGasSymbol(token.chainId)} for gas`
    }
    return true
  }

  const onSubmit = ({ amount }: { amount: string }) => {
    const parsed = toTokenAmount(amount)
    if (parsed === null) return
    transfer.sendTransfer({
      tokenAddress: EvmAddress.format(token.address),
      chainId: token.chainId,
      amount: parsed,
    })
  }

  const setAmountToMax = () => {
    maxTokenAmount.current = token.amount
    setValue(
      'amount',
      isUsdMode ? toUsdValue(token.amount).toFixed(2) : availableAmount,
      { shouldValidate: true },
    )
  }

  // Rewrite the field in the new denomination so the amount the user picked
  // survives the switch.
  const switchDenomination = () => {
    const parsed = toTokenAmount(getValues('amount'))
    toggleDenomination()
    if (parsed === null || parsed <= 0n) return
    setValue(
      'amount',
      isUsdMode ? formatUnits(parsed, decimals) : toUsdValue(parsed).toFixed(2),
    )
  }

  const convertedAmount = toTokenAmount(amountValue)
  const convertedLabel =
    convertedAmount && convertedAmount > 0n
      ? isUsdMode
        ? `${formatUnits(convertedAmount, decimals)} ${symbol}`.trim()
        : displayCurrency(toUsdValue(convertedAmount))
      : isUsdMode
        ? symbol
        : 'USD'

  return (
    <div className="space-y-4">
      <List
        items={[
          {
            key: 'selected-asset',
            content: (
              <TokenDisplay
                layout="two-line"
                address={token.address}
                chainId={token.chainId}
              />
            ),
            meta: (
              <TokenBalance
                address={token.address}
                chainId={token.chainId}
                tokenAmount={token.amount}
                layout="standard"
                size="small"
                direction="neutral"
              />
            ),
            tooltip: 'Choose a different asset',
            onClick: onBack,
          },
        ]}
      />
      <NumberInput
        label="Amount"
        control={control}
        inputName="amount"
        placeholder={isUsdMode ? '$0.00' : '0'}
        prefix={isUsdMode ? '$' : undefined}
        decimalScale={isUsdMode ? 2 : decimals}
        validationFunc={validateAmount}
        onChange={() => {
          maxTokenAmount.current = null
        }}
        footer={
          <div className="flex items-center justify-between text-xs text-secondary">
            {canShowUsd ? (
              <button
                type="button"
                className="flex items-center gap-1 rounded-md hover:text-primary"
                onClick={switchDenomination}
              >
                <ArrowUpDown className="size-3" />
                <span>{convertedLabel}</span>
              </button>
            ) : (
              <div />
            )}
            <div className="flex items-center space-x-2">
              <div>
                {isUsdMode
                  ? displayCurrency(toUsdValue(token.amount))
                  : availableAmount}{' '}
                available
              </div>
              <button
                type="button"
                className="cursor-pointer rounded-xl border border-gray-500 px-2"
                onClick={setAmountToMax}
              >
                Max
              </button>
            </div>
          </div>
        }
      />
      {noGasReason && (
        <Message variant="warning" title="Connected wallet needs gas">
          {noGasReason}
        </Message>
      )}
      {transfer.errorMessage && (
        <Message variant="danger" title="Deposit failed">
          {transfer.errorMessage}
        </Message>
      )}
      <div className="flex justify-end">
        <Button
          eventName="clickedSendWalletDeposit"
          isLoading={transfer.isTransferring}
          isDisabled={!!noGasReason}
          onClick={handleSubmit(onSubmit)}
        >
          {getSendButtonLabel({
            isTransferring: transfer.isTransferring,
            transferPhase: transfer.transferPhase,
          })}
        </Button>
      </div>
    </div>
  )
}
